import React, { useMemo, lazy, Suspense, useState } from "react";
import { Fragment } from "../../../interfaces/Publication";
import { ButtonsRight } from "../../../interfaces/UI";
import VirtualFragmentContainer from "./VirtualFragmentContainer";
import Anchor from "./Anchor";

import Quote from "./Quote";
import Credits from "./Credits";
import TXTIMG from "./TXTIMG";
import IMGTXT from "./IMGTXT";
import TXT_IM from "./TXT_IM";
import IM_TXT from "./IM_TXT";
import IMG from "./IMG";
import TXTTXT from "./TXTTXT";
import CONTENT from "./CONTENT";
import ARTICLE from "./ARTICLE";
import EDITORIAL from "./EDITORIAL";
import HTML from "./HTML";
import REFERENCIA from "./REFERENCIA";
const INF = lazy(() => import("./INF"));
const TXT = lazy(() => import("./TXT"));
const OVA = lazy(() => import("./OVA"));
const VIDEO = lazy(() => import("./VIDEO"));
const LAB = lazy(() => import("./LAB"));
const PERFIL = lazy(() => import("./PERFIL"));
const GALERY = lazy(() => import("./GALERY"));
const BIBLIOGRAPHY = lazy(() => import("./BIBLIOGRAPHY"));
const VIewer3Djs = lazy(() => import("./VIewer3Djs"));
const TABLE = lazy(() => import("./TABLE"));
const GRAPH = lazy(() => import("./GRAPH"));

interface FragmentContentProps {
  fragment: Fragment;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
  rawMode?: boolean;
  index: number;
  textOffset?: number; 
  subsectionId?: number;
  isFirstFragment?: boolean; // <-- AGREGAR ESTA PROP
}

class FragmentErrorBoundary extends React.Component<
  { children: React.ReactNode; onRetry: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onRetry: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Fragment component failed to load:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            background: "#ffe6e6",
            color: "#cc0000",
            border: "1px solid #ffcccc",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          <div>Failed to load component</div>
          <button
            onClick={this.props.onRetry}
            style={{
              marginTop: "10px",
              padding: "5px 10px",
              background: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const FragmentLoading = ({ fragmentType }: { fragmentType: string }) => {
  const isHeavy = [
    "OVA", "INF", "VIDEO", "LAB", "PERFIL", "GALERY", "SKF", "TABLE", "GRAPH", "TXT"
  ].includes(fragmentType);

  return (
    <div
      style={{
        padding: isHeavy ? "40px 20px" : "20px",
        textAlign: "center",
        color: "#666",
        minHeight: isHeavy ? "200px" : "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isHeavy ? "#f9f9f9" : "transparent",
        border: isHeavy ? "1px dashed #ddd" : "none",
        borderRadius: isHeavy ? "8px" : "0",
      }}
    >
      Loading...
    </div>
  );
};

const useFragmentTypes = () => {
  return useMemo(() => ({
    TXT: TXT,
    QUOTE: Quote,
    CREDIT: Credits,
    TXTIMG: TXTIMG,
    IMGTXT: IMGTXT,
    TXT_IM: TXT_IM,
    "TXT-IM": TXT_IM,
    IM_TXT: IM_TXT,
    "IM-TXT": IM_TXT,
    IMG: IMG,
    CONTENT: CONTENT,
    ARTICLE: ARTICLE,
    EDITORIAL: EDITORIAL,
    HTML: HTML,
    REFERENCIA: REFERENCIA,
    INF: INF,
    OVA: OVA,
    VIDEO: VIDEO,
    LAB: LAB,
    PERFIL: PERFIL,
    GAL: GALERY,
    BBL: BIBLIOGRAPHY,
    SKF: VIewer3Djs,
    TABLE: TABLE,
    GRAPH: GRAPH,
    PRC: TXT,
  }), []);
};

const FragmentContent = ({
  fragment,
  openButtons,
  setOpenButtons,
  rawMode = false,
  index,
  textOffset = 0,
  subsectionId,
  isFirstFragment = false // <-- VALOR POR DEFECTO
}: FragmentContentProps) => {
  const fragmentTypes = useFragmentTypes();
  const [retryCount, setRetryCount] = useState(0);

  const isHeavyFragment = useMemo(
    () => ["OVA", "INF", "VIDEO", "LAB", "PERFIL", "GALERY", "SKF", "TABLE", "GRAPH", "TXT"]
      .includes(fragment.type),
    [fragment.type]
  );

  const fragmentProps = useMemo(
    () => ({ 
      fragment, 
      openButtons, 
      setOpenButtons, 
      rawMode,
      index,
      fragmentId: fragment.id.toString(),
      textOffset,
      subsectionId,
      isFirstFragment // <-- PASAR AL COMPONENTE
    }),
    [fragment, openButtons, setOpenButtons, rawMode, index, textOffset, subsectionId, isFirstFragment]
  );

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const FragmentComponent = useMemo(() => {
    const Component = fragmentTypes[fragment.type];
    if (!Component) {
      const NotFoundComponent = () => (
        <div id={`${fragment.type}${fragment.id}`}>
          Component {fragment.type} not created yet.
        </div>
      );
      return NotFoundComponent;
    }
    return Component;
  }, [fragment.type, fragment.id, fragmentTypes, retryCount]);

  const renderedComponent = (
    <Suspense fallback={<FragmentLoading fragmentType={fragment.type} />}>
      <FragmentErrorBoundary onRetry={handleRetry}>
        <FragmentComponent
          {...fragmentProps}
          key={`${fragment.id}-${retryCount}`}
        />
      </FragmentErrorBoundary>
    </Suspense>
  );

  if (isHeavyFragment) {
    return (
      <VirtualFragmentContainer
        fragment={fragment}
        openButtons={openButtons}
        setOpenButtons={setOpenButtons}
        isHeavy={true}
      >
        {renderedComponent}
      </VirtualFragmentContainer>
    );
  }

  return (
    <div id={`${fragment.type}${fragment.id}`}>
      <Anchor name={`${fragment.type}${fragment.id}`} />
      {renderedComponent}
    </div>
  );
};

export default React.memo(
  FragmentContent,
  (prevProps, nextProps) =>
    prevProps.fragment.id === nextProps.fragment.id &&
    prevProps.fragment.type === nextProps.fragment.type &&
    prevProps.fragment.text === nextProps.fragment.text &&
    prevProps.fragment.title === nextProps.fragment.title &&
    prevProps.textOffset === nextProps.textOffset &&
    prevProps.subsectionId === nextProps.subsectionId &&
    prevProps.isFirstFragment === nextProps.isFirstFragment // <-- AGREGAR AL COMPARADOR
);